

import React, { useState, useCallback, useRef, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Loader2, CheckCircle, XCircle, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getOrSetSessionId } from "@/lib/session";
import type { ParseResumeDetailsOutput } from "@/types/ai";
import { logAiFeatureUsage } from "@/lib/logging";



const MAX_FILE_SIZE_MB = 5;
type UploadStage = "idle" | "readingFile" | "extractingDetails" | "success" | "error";





export const AnimatedDropbox = ({
  onFileUploadComplete,
  isParentProcessing
}: {
  onFileUploadComplete: (
    success: boolean,
    fileName: string | null,
    parsedDetails: ParseResumeDetailsOutput | null,
    maskedResumeText: string | null,
    resumeId: string | null,
    sessionToken: string | null
  ) => void;
  isParentProcessing: boolean;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadState, setUploadState] = useState<UploadStage>("idle");
  const [currentFileName, setCurrentFileName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [hasPassedCaptcha, setHasPassedCaptcha] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const captchaRef = useRef<ReCAPTCHA | null>(null);
  const currentUploadStageRef = useRef<UploadStage>(uploadState);
  const { toast } = useToast();

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    setHasPassedCaptcha(!!token);
    
    if (token) {
      toast({
        title: "CAPTCHA Completed",
        description: "You can now upload your resume!",
        duration: 3000,
      });
    }
  };

  const handleError = useCallback((message: string, stage: UploadStage = currentUploadStageRef.current) => {
    setUploadState("error");
    setErrorMessage(message);
    setCurrentFileName(null);
    toast({
      variant: "destructive",
      title: "Upload Failed!",
      description: `During ${stage}: ${message.length > 100 ? message.substring(0, 97) + "..." : message}`
    });
    onFileUploadComplete(false, null, null, null, null);
    fileInputRef.current && (fileInputRef.current.value = "");
    setTimeout(() => {
      setUploadState("idle");
      setErrorMessage(null);
    }, 5000);
  }, [toast, onFileUploadComplete]);

  useEffect(() => {
    currentUploadStageRef.current = uploadState;
  }, [uploadState]);



  const processFile = useCallback(async (fileToProcess: File) => {
    setCurrentFileName(fileToProcess.name);
    setErrorMessage(null);
    currentUploadStageRef.current = "idle";

    const allowedExtensions = [".pdf", ".docx", ".doc"];
    const fileExtension = fileToProcess.name.substring(fileToProcess.name.lastIndexOf(".")).toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      handleError(`Invalid file type (${fileExtension}). Please use PDF, DOC, or DOCX.`, "idle");
      return;
    }

    if (fileToProcess.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      handleError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`, "idle");
      return;
    }

    setUploadState("readingFile");

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('resume', fileToProcess);
      formData.append('recaptchaToken', captchaToken || '');
      formData.append('sessionId', getOrSetSessionId());

      setUploadState("extractingDetails");
      
      // Upload file to backend API
      const response = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/upload-resume`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        handleError(errorData.error || "Failed to upload and parse resume.", "extractingDetails");
        return;
      }

      const result = await response.json();
      
      if (!result.success) {
        handleError(result.error || "Upload failed.", "extractingDetails");
        return;
      }

      const { resumeId, fileName, parsedDetails, sessionToken } = result;



      // Log AI feature usage
      logAiFeatureUsage({
        feature: "parseResumeDetails",
        input: { fileName: fileToProcess.name, fileSize: fileToProcess.size },
        output: parsedDetails,
        sessionId: getOrSetSessionId()
      });

      onFileUploadComplete(true, fileName, parsedDetails, parsedDetails.maskedResumeText ?? null, resumeId, sessionToken);
      setUploadState("success");

      captchaRef.current?.reset();
      setCaptchaToken(null);
      setHasPassedCaptcha(false);
    } catch (error: any) {
      handleError(error?.message ?? "Unknown processing error.", currentUploadStageRef.current);
    }
  }, [handleError, toast, onFileUploadComplete, captchaToken]);

  const handleClick = () => {
    if ((uploadState === "idle" || uploadState === "error") && !isParentProcessing) {
      if (!hasPassedCaptcha) {
        toast({
          variant: "destructive",
          title: "CAPTCHA Required",
          description: "Please complete the CAPTCHA before uploading."
        });
        return;
      }
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isParentProcessing || ["readingFile", "extractingDetails"].includes(uploadState)) {
      fileInputRef.current && (fileInputRef.current.value = "");
      return;
    }
    const file = e.target.files?.[0] ?? null;
    if (file) processFile(file);
  };

  const isBusy = ["readingFile", "extractingDetails"].includes(uploadState);
  const isGloballyBusy = isBusy || isParentProcessing;

  const getProcessingStepText = () => {
    switch (uploadState) {
      case "readingFile": return "Reading file...";
      case "parsingText": return "Parsing resume text...";
      case "extractingDetails": return "Structuring & Masking PII (AI)...";
      default: return "Processing...";
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      {!hasPassedCaptcha && uploadState !== "success" && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 flex flex-col items-center w-full"
        >
          <p className="text-sm text-muted-foreground mb-4 text-center">
            Please complete the verification to upload your resume
          </p>
          <div className="transform scale-90 md:scale-100">
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
              onChange={handleCaptchaChange}
              theme="dark"
              size="normal"
            />

          </div>
        </motion.div>
      )}

      <motion.button
        type="button"
        onClick={handleClick}
        disabled={isGloballyBusy}
        onDragOver={(e) => { e.preventDefault(); if (!isGloballyBusy) setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          if (!hasPassedCaptcha) {
            toast({
              variant: "destructive",
              title: "Verification Required",
              description: "Please complete the verification before uploading."
            });
            return;
          }
          if (isGloballyBusy) return;
          const file = e.dataTransfer?.files?.[0];
          if (file) processFile(file);
        }}
        className={cn(
          "relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all duration-300 ease-out w-full min-h-[200px] p-8",
          "hover:border-primary/50 hover:bg-muted/20",
          isDragging && !isGloballyBusy ? "border-primary bg-primary/5 scale-[1.02] shadow-lg" : "border-border",
          uploadState === "success" ? "border-green-500/50 bg-green-500/5" : "",
          uploadState === "error" ? "border-red-500/50 bg-red-500/5" : "",
          isBusy ? "border-primary/50 bg-primary/5 cursor-default" : "cursor-pointer",
          isParentProcessing && "cursor-not-allowed opacity-60",
          !hasPassedCaptcha && uploadState === "idle" ? "bg-muted/20 opacity-50 pointer-events-none" : "",
          hasPassedCaptcha && uploadState === "idle" ? "border-primary/30 bg-primary/5" : ""
        )}
        data-upload-state={uploadState}
        style={{ pointerEvents: isGloballyBusy ? "none" : "auto" }}
        whileHover={!isGloballyBusy ? { scale: 1.01 } : {}}
        whileTap={!isGloballyBusy ? { scale: 0.99 } : {}}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept=".pdf,.doc,.docx"
          disabled={isGloballyBusy}
        />

        <motion.div
          key={uploadState}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="flex flex-col items-center text-center px-4 w-full"
        >
          {uploadState === "idle" && (
            <>
              <motion.div 
                className="w-16 h-16 rounded-full bg-gradient-to-r from-primary to-primary/80 flex items-center justify-center mb-6 shadow-lg"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Plus className="h-8 w-8 text-primary-foreground" />
              </motion.div>
              <h3 className="font-semibold text-lg mb-2">
                {hasPassedCaptcha ? "Upload Your Resume" : "Complete Verification"}
              </h3>
              <p className="text-muted-foreground text-sm mb-2">
                {hasPassedCaptcha ? "Drag and drop or click to browse" : "Please complete the verification above"}
              </p>
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <span>Supported formats:</span>
                <span className="px-2 py-1 bg-muted rounded-md">PDF</span>
                <span className="px-2 py-1 bg-muted rounded-md">DOCX</span>
                <span className="px-2 py-1 bg-muted rounded-md">DOC</span>
              </div>
            </>
          )}
          {isBusy && (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 rounded-full bg-gradient-to-r from-primary to-primary/80 flex items-center justify-center mb-6 shadow-lg"
              >
                <Loader2 className="h-8 w-8 text-primary-foreground" />
              </motion.div>
              <h3 className="font-semibold text-lg mb-2">{getProcessingStepText()}</h3>
              {currentFileName && (
                <p className="text-sm text-muted-foreground break-all max-w-full truncate">
                  {currentFileName}
                </p>
              )}
            </>
          )}
          {uploadState === "success" && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                className="w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center mb-6 shadow-lg"
              >
                <CheckCircle className="h-8 w-8 text-white" />
              </motion.div>
              <h3 className="font-semibold text-lg mb-2 text-green-600">Resume Ready!</h3>
              {currentFileName && (
                <p className="text-sm text-muted-foreground break-all max-w-full truncate">
                  {currentFileName}
                </p>
              )}
            </>
          )}
          {uploadState === "error" && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                className="w-16 h-16 rounded-full bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center mb-6 shadow-lg"
              >
                <XCircle className="h-8 w-8 text-white" />
              </motion.div>
              <h3 className="font-semibold text-lg mb-2 text-red-600">Upload Failed</h3>
              {errorMessage && (
                <p className="text-sm text-muted-foreground max-w-full break-words">
                  {errorMessage}
                </p>
              )}
            </>
          )}
        </motion.div>
      </motion.button>
    </div>
  );
};
